import {useCallback, useContext, useMemo, useState} from 'react'

import {OperatorChartSettingsContext} from './settingsContext'

export const useLocalStorageSettings = <T extends Record<string, unknown>>(
  settingKey: string,
  initial: T
): {settings: T; updateSettings: (newSettings: T) => void} => {
  const scope = useContext(OperatorChartSettingsContext)
  const key = scope ? `${scope}:${settingKey}` : settingKey
  const memoizedInitial = useMemo(() => initial, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getFromStorage = useCallback((): T | undefined => {
    try {
      const raw = localStorage.getItem(key)
      return raw ? (JSON.parse(raw) as T) : undefined
    } catch {
      return undefined
    }
  }, [key])

  const setToStorage = useCallback(
    (value: T) => {
      try {
        localStorage.setItem(key, JSON.stringify(value))
      } catch {
        // ignore storage errors
      }
    },
    [key]
  )

  const [state, setState] = useState<T>(() => getFromStorage() ?? memoizedInitial)

  const updateSettings = useCallback(
    (newSettings: T) => {
      setState(newSettings)
      setToStorage(newSettings)
    },
    [setToStorage]
  )

  return {settings: state, updateSettings}
}
